import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle, BarChart3, CheckCheck, ChevronDown, ChevronLeft, ChevronRight,
  Coins, Clock3, Download, Inbox, Info, Loader2, RefreshCw, Search,
  SlidersHorizontal,
  Ticket, TrendingUp, Users, Wand2, X,
} from 'lucide-react';
import client from '../lib/api';
import DateRangeFilter from './components/DateRangeFilter';
import { EMPTY_DATE_RANGE, dateRangeParams } from './lib/dateRange';
import { formatDateTime, formatMoney } from './lib/formatters';
import { PAGE_SIZES, PAGE_SIZE_OPTIONS, pageWindow, readPageSize, writePageSize }
  from './lib/pagination';
import ConfirmDialog from './components/ConfirmDialog';
import { useAdminPermissions } from './lib/permissions';
import {
  PICK_RESULT_CLASS, PROVIDER_LABELS, SETTLE_ACTIONS, SETTLE_PERMISSION,
  STATUS_LABELS, pickLabel, settleAction, settleSummaryText,
} from './lib/sportsBets';
import HostedBetSelections from './components/HostedBetSelections';
import { PENDING_HINT, hostedBadge } from './lib/hostedBets';
import { useHostedBetDetails } from './lib/useHostedBetDetails';

// Bütün üyelerin spor kuponları — Spor Bahisleri sayfasının listesi.
//
// Üye profilindeki Bahis Geçmişi ekranı tek bir hesabı gösterir; bu liste "kim
// ne oynamış" sorusunu üye seçmeden yanıtlar. İkinci kullanım yeri **özel
// orandır**: yönetici bir seçime kendi fiyatını geçirdiğinde onu binlerce üye
// içinden kimin oynadığı ancak buradan görülür — Özel Oranlar bölümündeki
// "Kuponlar" düğmesi sayfayı o tanımın imzasıyla açar.
//
// Süzgeç kuponun ``picks`` JSON'una uygulanamaz (ORM katmanı JSON içinde arama
// yapmaz); backend kupon açılırken ``custom_odd_count`` / ``custom_odd_keys``
// kolonlarını doldurur. Bu kolonlardan **önce** açılmış kuponlarda iz yoktur.
// Maç, lig ve bacak bazlı süzgeçler ayrı bir bacak tablosuna (``coupon_picks``)
// uygulanır.
//
// Bileşen yalnızca çizer ve süzgeçleri taşır; para kararını `bulk-settle` ucu
// verir. Yerleşim `sportsBets.css` içindedir.

// Seçenek kümesi panelin bütün liste ekranlarıyla ortaktır (bkz.
// lib/pagination); ekran başına bir liste tutmak aynı panelde iki farklı
// tavan göstermek olurdu.
export { PAGE_SIZES };

// Bahis türü ile ödeme durumu **ayrı sorulardır**: birincisi paranın kasadan
// çıkıp çıkmadığını (freebet kuponunda çıkmaz), ikincisi kasadan para ödenip
// ödenmediğini sorar. Sonuçlanmış ama ödemesiz kupon (kayıp) ile henüz
// sonuçlanmamış kupon aynı şey değildir; ikisi de "ödenmemiş" görünür.
const STAKE_TYPES = [
  { value: 'all', label: 'Nakit + Freebet' },
  { value: 'cash', label: 'Yalnızca nakit' },
  { value: 'freebet', label: 'Yalnızca freebet' },
];
const PAYOUT_STATES = [
  { value: 'all', label: 'Tüm ödemeler' },
  { value: 'paid', label: 'Ödeme yapıldı' },
  { value: 'unpaid', label: 'Ödeme yok (kapandı)' },
  { value: 'pending', label: 'Ödeme bekliyor' },
];
// Durum kümesi backend'in kupon durumlarıyla aynıdır; ayrışırsa uç 400 döner.
const STATUS_OPTIONS = [
  { value: 'all', label: 'Tüm durumlar' },
  { value: 'active', label: 'Beklemede' },
  { value: 'won', label: 'Kazandı' },
  { value: 'lost', label: 'Kaybetti' },
  { value: 'cashed_out', label: 'Cashout' },
];

// Kırılım raporunun grupları; anahtarlar backend'in COUPON_REPORT_GROUPS
// kümesiyle aynı olmalıdır, ayrışırsa uç 400 döner.
const REPORT_GROUPS = [
  { value: 'league', label: 'Lige göre' },
  { value: 'sport', label: 'Spor dalına göre' },
  { value: 'provider', label: 'Oran kaynağına göre' },
];
const REPORT_GROUP_LABELS = {
  league: 'LİG', sport: 'SPOR DALI', provider: 'KAYNAK',
};

const optionLabel = (options, value) => (
  options.find((item) => item.value === value)?.label || value
);

// Dışa aktarım blob olarak istendiği için hata gövdesi de blob gelir; JSON
// olarak okunmazsa yönetici sebebi göremez ("40.000 kupon uyuyor, tarih
// aralığını daraltın" yerine yalnızca sessiz bir başarısızlık). Blob.text()
// her ortamda bulunmadığından FileReader'a düşülür.
const readErrorDetail = async (error) => {
  const body = error?.response?.data;
  if (!body) return '';
  let text = '';
  try {
    if (typeof body === 'string') {
      text = body;
    } else if (typeof body.text === 'function') {
      text = await body.text();
    } else {
      text = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => resolve('');
        reader.readAsText(body);
      });
    }
    return JSON.parse(text || '{}')?.detail || '';
  } catch {
    return '';
  }
};

// Tarih hücresi iki satırdır: gün üstte, saat altta. Tek satıra sıkıştırılan
// "20.08.26 10:00" sütunu, yanındaki üye adını ezecek kadar genişletiyordu.
const dateParts = (value) => {
  if (!value) return { day: '—', time: '' };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { day: formatDateTime(value), time: '' };
  return {
    day: date.toLocaleDateString('tr-TR', {
      day: '2-digit', month: '2-digit', year: '2-digit',
    }),
    time: date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
  };
};

const initials = (name) => (
  String(name || '').trim().slice(0, 2).toLocaleUpperCase('tr-TR') || '—'
);

// Kupon satırının kasaya etkisi. Sitenin kendi kuponunda ``pnl``, hazır
// kuponda ``net`` alanındadır; ikisi de "ödenen − kasadan çıkan"dır ve
// freebet/iade edilen bahis nete girmez. Alan adları ayrı olduğu için tek
// yerde çözülür, yoksa sütun bir kaynakta boş kalırdı.
const couponNet = (coupon) => {
  const value = coupon.source === 'hosted' ? coupon.net : coupon.pnl;
  return Number.isFinite(Number(value)) ? Number(value) : null;
};

const Stat = ({ label, value, hint, tone = 'neutral', icon: Icon, toned = false }) => (
  <div className={`sbet-stat tone-${tone} ${toned ? 'is-toned' : ''}`}>
    <div className="sbet-stat-label">
      {Icon && <Icon size={12} aria-hidden="true" />}
      {label}
    </div>
    <div className="sbet-stat-value">{value}</div>
    {hint && <div className="sbet-stat-hint">{hint}</div>}
  </div>
);

// Süzgeç kutusu + üstündeki etiket. Etiket görsel bir başlıktır; kontrolün
// erişilebilir adı kendi `aria-label`'ındadır — kutunun içinde bir `<form>`
// olabildiği için sarmalayıcı `<label>` olamaz (geçersiz içerik modeli).
const Field = ({ label, children }) => (
  <div className="sbet-field">
    <span aria-hidden="true">{label}</span>
    {children}
  </div>
);

// Süzgeçler iki katmandır: en sık sorulan beşi (üye, kupon no, durum, ödeme,
// maç) her zaman görünür, kalanı *Detaylı süzgeç* düğmesinin altındadır.
// On iki kutuluk şerit listeyi ekranın altına itiyordu ve yöneticinin her gün
// kullandığı kutular seyrek kullanılanların arasında kayboluyordu.
//
// Detay çekmecesinin açık/kapalı tercihi **tarayıcıda** durur: sunucuya
// yazılsaydı bir yöneticinin kapattığı çekmece diğerininkini de kapatırdı
// (Herkül'ün sohbet rayıyla aynı yaklaşım). Varsayılan kapalıdır; içinde
// etkin bir süzgeç varsa düğme sayısını taşır ve çipler her zaman görünür.
const FILTERS_MORE_KEY = 'admin:sbet-filters-more';

const readMoreOpen = () => {
  try {
    return window.localStorage.getItem(FILTERS_MORE_KEY) === '1';
  } catch {
    return false;
  }
};

// Kupon numarası listede kısaltılmış görünür: sitenin kuponunda kimlik UUID'dir
// (36 karakter) ve tablo sütununu taşırırdı; ilk sekiz hane tekil tanımaya
// yeter ve arama kutusu parça numarayla da bulur. Hazır kuponun numarası
// sağlayıcınındır ve olduğu gibi yazılır — sağlayıcıyla konuşulurken o numara
// sorulur.
export const couponNumber = (coupon) => {
  if (coupon.source === 'hosted') return String(coupon.bet_id || '');
  return String(coupon.id || '').slice(0, 8).toUpperCase();
};

const couponFullNumber = (coupon) => (
  coupon.source === 'hosted' ? String(coupon.bet_id || '') : String(coupon.id || '')
);

const CouponNo = ({ coupon }) => {
  const [copied, setCopied] = useState(false);
  const full = couponFullNumber(coupon);
  if (!full) return <span className="sbet-user-none">—</span>;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      /* pano izni yoksa numara yine ekranda okunur */
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="sbet-no"
      title={`Kupon numarası: ${full} — kopyalamak için tıklayın`}
    >
      {copied ? 'Kopyalandı' : `#${couponNumber(coupon)}`}
    </button>
  );
};

const SportsCoupons = ({ override = null, onClearOverride }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => readPageSize('coupons'));
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [couponQuery, setCouponQuery] = useState('');
  const [couponNo, setCouponNo] = useState('');
  const [status, setStatus] = useState('all');
  const [stakeType, setStakeType] = useState('all');
  const [payoutState, setPayoutState] = useState('all');
  const [minStake, setMinStake] = useState('');
  const [maxStake, setMaxStake] = useState('');
  const [teamQuery, setTeamQuery] = useState('');
  const [team, setTeam] = useState('');
  const [league, setLeague] = useState('');
  const [sportKey, setSportKey] = useState('');
  const [provider, setProvider] = useState('');
  const [facets, setFacets] = useState({ leagues: [], sports: [], providers: [] });
  const [customOnly, setCustomOnly] = useState(false);
  const [dateRange, setDateRange] = useState(EMPTY_DATE_RANGE);
  const [moreOpen, setMoreOpen] = useState(readMoreOpen);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [hostedSupported, setHostedSupported] = useState(true);
  const [report, setReport] = useState(null);
  const [reportGroup, setReportGroup] = useState('league');
  // Toplu sonuçlandırma yalnızca sonuçlanmamış kuponlara uygulanır; seçim
  // sayfa değişince temizlenir, yoksa yönetici göremediği kuponlara karar
  // verirdi.
  const [selected, setSelected] = useState(() => new Set());
  const [settleRequest, setSettleRequest] = useState(null);
  const [settling, setSettling] = useState(false);
  const [settleNote, setSettleNote] = useState('');
  const [settleResult, setSettleResult] = useState('');
  const { can } = useAdminPermissions();
  const canSettle = can(SETTLE_PERMISSION);

  // Özel oran satırından gelindiğinde liste o tanıma kilitlenir; sayfa da
  // başa döner, yoksa yönetici boş bir sayfada "kimse oynamamış" sanır.
  useEffect(() => { if (override) setPage(0); }, [override]);

  // Süzgeçler liste ve dışa aktarımda **aynı** olmalıdır: ayrışırlarsa
  // yöneticinin ekranda saydığı kupon ile indirdiği dosya tutmaz. Backend de
  // ikisini tek fonksiyondan okur (admin_router._coupon_filters).
  const filterParams = useCallback(() => {
    const params = { ...dateRangeParams(dateRange) };
    if (search) params.q = search;
    if (couponNo) params.coupon_no = couponNo;
    if (status !== 'all') params.status = status;
    if (stakeType !== 'all') params.stake_type = stakeType;
    if (payoutState !== 'all') params.payout_state = payoutState;
    if (minStake !== '') params.min_stake = Number(minStake);
    if (maxStake !== '') params.max_stake = Number(maxStake);
    // Takım / lig / spor dalı süzgeci kuponun JSON'una değil bacak tablosuna
    // uygulanır; kupon en az bir bacağı uyduğunda listeye girer.
    if (team) params.team = team;
    if (league) params.league = league;
    if (sportKey) params.sport_key = sportKey;
    if (provider) params.provider = provider;
    if (override?.signature) params.override = override.signature;
    else if (customOnly) params.custom = true;
    return params;
  }, [
    couponNo, customOnly, dateRange, league, maxStake, minStake, override,
    payoutState, provider, search, sportKey, stakeType, status, team,
  ]);

  // Lig ve spor dalı listesi sabit değildir: hangi ligin oynandığı
  // sağlayıcıya ve panelde açık bırakılan yarışmalara bağlıdır.
  useEffect(() => {
    let alive = true;
    client.get('/admin/coupons/facets')
      .then(({ data }) => {
        if (alive) setFacets({
          leagues: data.leagues || [],
          sports: data.sports || [],
          providers: data.providers || [],
        });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Kaynak adları uçtan gelir (hazır spor kitabının görünen adı dahil);
  // sabit sözlük yalnızca eski kayıtların yedeğidir.
  const providerLabel = useCallback((value) => (
    facets.providers.find((row) => row.value === value)?.label
      || PROVIDER_LABELS[value] || value
  ), [facets.providers]);

  // Sitenin kendi oran beslemesinden hiç kupon yoksa (kurulum yalnızca hazır
  // spor kitabı kullanıyorsa) yalnızca o kuponlara anlam veren süzgeçler —
  // freebet ayrımı, özel oran, kırılım raporu — çizilmez: her biri
  // seçildiğinde listeyi boşaltıyordu ve yönetici "kupon yok" sanıyordu.
  const hasNative = facets.providers.some(
    (row) => Object.prototype.hasOwnProperty.call(PROVIDER_LABELS, row.value));

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const { data } = await client.get('/admin/coupons', {
        params: { skip: page * pageSize, limit: pageSize, ...filterParams() },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setSummary(data.summary || null);
      setHostedSupported(data.hosted_supported !== false);
    } catch (error) {
      setItems([]);
      setTotal(0);
      setSummary(null);
      // Arıza bir süre yutuluyordu ve ekran "süzgece uyan kupon bulunamadı"
      // diyordu: yönetici olmayan bir sonucu gerçek sanıyordu.
      setLoadError(
        error?.response?.data?.detail || 'Kupon listesi alınamadı.');
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  // Hazır spor kitabı kuponları (Flexrix / Betby) aynı listededir ve içerikleri
  // para hareketiyle gelmez; eksik olanlar sağlayıcıdan tek tek istenir.
  const { busy, errors, retry } = useHostedBetDetails(items, setItems);

  // Kırılım yalnızca yönetici açtığında çekilir: liste her süzgeç
  // değişiminde yenilenir, rapor sorgusu ise GROUP BY taraması yapar.
  const loadReport = useCallback(async (group) => {
    setReport({ group, loading: true, items: [] });
    try {
      const { data } = await client.get('/admin/coupons/report', {
        params: { group, ...filterParams() },
      });
      setReport({ group, loading: false, items: data.items || [] });
    } catch {
      setReport({ group, loading: false, items: [], error: true });
    }
  }, [filterParams]);

  // Rapor açıkken süzgeç değişirse listeyle birlikte tazelenir; yoksa iki
  // ekran aynı anda iki farklı pencereyi anlatırdı.
  useEffect(() => {
    if (report) loadReport(reportGroup);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams, reportGroup]);

  // Dosya yetkili bir istekle alınır (uç token ister), bu yüzden adres
  // doğrudan bir bağlantıya verilemez; yanıt blob olarak indirilir.
  const exportCsv = async () => {
    setExporting(true);
    setExportError('');
    try {
      const response = await client.get('/admin/coupons/export', {
        params: filterParams(),
        responseType: 'blob',
      });
      const stamp = new Date().toISOString().slice(0, 10);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `spor-bahisleri-${stamp}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Sınır aşımında backend sebebi yazar (tarih aralığını daraltın gibi);
      // okunamıyorsa genel mesaj kalır.
      setExportError(await readErrorDetail(error) || 'Dosya indirilemedi.');
    } finally {
      setExporting(false);
    }
  };

  const submitSearch = (event) => {
    event.preventDefault();
    setPage(0);
    setSearch(query.trim());
  };

  const submitCouponNo = (event) => {
    event.preventDefault();
    setPage(0);
    setCouponNo(couponQuery.trim().replace(/^#/, ''));
  };

  const submitTeam = (event) => {
    event.preventDefault();
    setPage(0);
    setTeam(teamQuery.trim());
  };

  const clearLegFilters = () => {
    setPage(0);
    setTeam(''); setTeamQuery('');
    setLeague(''); setSportKey(''); setProvider('');
  };

  // Sayfa ya da süzgeç değişince seçim düşer: yönetici artık göremediği bir
  // kupona karar verememelidir.
  useEffect(() => { setSelected(new Set()); }, [page, filterParams]);

  // Etkin süzgeçlerin çip listesi. Süzgeçler iki gruba ayrıldığı için hangi
  // kutuların dolu olduğu tek bakışta görünmüyordu; boş liste "kupon yok"
  // sanılıyordu. Her çip kendi süzgecini kaldırır ve sayfayı başa alır.
  const activeFilters = useMemo(() => {
    const chips = [];
    const push = (key, label, clear) => chips.push({ key, label, clear });
    if (search) {
      push('q', `Üye: ${search}`, () => { setSearch(''); setQuery(''); });
    }
    if (couponNo) {
      push('no', `Kupon: #${couponNo}`,
        () => { setCouponNo(''); setCouponQuery(''); });
    }
    if (status !== 'all') {
      push('status', optionLabel(STATUS_OPTIONS, status), () => setStatus('all'));
    }
    if (payoutState !== 'all') {
      push('payout', optionLabel(PAYOUT_STATES, payoutState),
        () => setPayoutState('all'));
    }
    if (stakeType !== 'all') {
      push('stake', optionLabel(STAKE_TYPES, stakeType),
        () => setStakeType('all'));
    }
    if (minStake !== '') {
      push('min', `Min ${minStake} ₺`, () => setMinStake(''));
    }
    if (maxStake !== '') {
      push('max', `Max ${maxStake} ₺`, () => setMaxStake(''));
    }
    if (customOnly && !override) {
      push('custom', 'Yalnızca özel oran', () => setCustomOnly(false));
    }
    if (team) push('team', `Maç: ${team}`, () => { setTeam(''); setTeamQuery(''); });
    if (league) push('league', `Lig: ${league}`, () => setLeague(''));
    if (sportKey) push('sport', `Spor: ${sportKey}`, () => setSportKey(''));
    if (provider) {
      push('provider', `Kaynak: ${providerLabel(provider)}`,
        () => setProvider(''));
    }
    if (dateRangeParams(dateRange).range) {
      push('date', 'Tarih aralığı', () => setDateRange(EMPTY_DATE_RANGE));
    }
    return chips;
  }, [
    couponNo, customOnly, dateRange, league, maxStake, minStake, override,
    payoutState, provider, providerLabel, search, sportKey, stakeType, status,
    team,
  ]);

  // Detay çekmecesindeki etkin süzgeç sayısı: çekmece kapalıyken düğme bunu
  // taşır, yoksa yönetici kısa listeyi "kupon yok" diye okurdu.
  const moreCount = [
    stakeType !== 'all', minStake !== '', maxStake !== '',
    customOnly && !override, !!league, !!sportKey, !!provider,
    !!dateRangeParams(dateRange).range,
  ].filter(Boolean).length;

  const toggleMore = () => setMoreOpen((current) => {
    const next = !current;
    try {
      window.localStorage.setItem(FILTERS_MORE_KEY, next ? '1' : '0');
    } catch {
      /* gizli sekmede depolama kapalı olabilir; tercih o oturumda yaşar */
    }
    return next;
  });

  const clearAllFilters = () => {
    setPage(0);
    setSearch(''); setQuery('');
    setCouponNo(''); setCouponQuery('');
    setStatus('all'); setPayoutState('all'); setStakeType('all');
    setMinStake(''); setMaxStake('');
    setCustomOnly(false);
    setDateRange(EMPTY_DATE_RANGE);
    clearLegFilters();
  };

  // Hazır spor kitabı kuponu panelden sonuçlandırılamaz (kupon sağlayıcıda
  // durur); toplu seçime hiç girmez.
  const openCoupons = items.filter(
    (coupon) => coupon.source !== 'hosted' && coupon.status === 'active');
  const toggleOne = (couponId) => setSelected((current) => {
    const next = new Set(current);
    if (next.has(couponId)) next.delete(couponId);
    else next.add(couponId);
    return next;
  });
  const allOpenSelected = openCoupons.length > 0
    && openCoupons.every((coupon) => selected.has(coupon.id));
  const toggleAll = () => setSelected(
    allOpenSelected ? new Set() : new Set(openCoupons.map((row) => row.id)));

  const runSettle = async () => {
    if (!settleRequest) return;
    setSettling(true);
    try {
      const { data } = await client.post('/admin/coupons/bulk-settle', {
        coupon_ids: settleRequest.couponIds,
        action: settleRequest.action,
        note: settleNote.trim(),
      });
      setSettleResult(settleSummaryText(data));
      setSelected(new Set());
      setSettleRequest(null);
      setSettleNote('');
      await load();
    } catch (error) {
      setSettleResult(
        error?.response?.data?.detail || 'Kuponlar sonuçlandırılamadı.');
    } finally {
      setSettling(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const rangeFrom = total ? page * pageSize + 1 : 0;
  const rangeTo = Math.min(total, (page + 1) * pageSize);

  return (
    <>
      {override && (
        <div className="sbet-note is-magic">
          <Wand2 size={15} aria-hidden="true" />
          <div className="sbet-note-body">
            <b>{override.label || override.signature}</b> özel oranını oynayan
            kuponlar listeleniyor.
          </div>
          <button
            type="button"
            onClick={onClearOverride}
            className="admin-btn-secondary sbet-note-action"
          >
            <X size={11} aria-hidden="true" /> Süzgeci kaldır
          </button>
        </div>
      )}

      {/* --- Özet -------------------------------------------------------- */}
      {/* Kartlar süzgece uyan **bütün** kuponları sayar, ekrandaki sayfayı
          değil: "bu hafta kaç kişi oynadı" sorusunun yanıtı buradadır. */}
      {summary && (
        <div className="sbet-stats">
          <Stat
            label="Kupon"
            value={summary.total_coupons}
            icon={Ticket}
            hint={total > pageSize
              ? `${rangeFrom}–${rangeTo} arası gösteriliyor` : ''}
          />
          <Stat
            label="Beklemede"
            value={summary.active ?? 0}
            tone="warn"
            toned
            icon={Clock3}
            hint="Henüz sonuçlanmamış kupon"
          />
          <Stat
            label="Tekil Oyuncu"
            value={summary.players}
            tone="info"
            toned
            icon={Users}
            hint="Kampanyanın kaç kişiye ulaştığı budur"
          />
          <Stat
            label="Toplam Bahis"
            value={formatMoney(summary.total_stake)}
            icon={Coins}
          />
          <Stat
            label="Toplam Ödeme"
            value={formatMoney(summary.total_payout)}
            tone="ok"
            toned
            icon={TrendingUp}
          />
          <Stat
            label="Net Sonuç"
            value={formatMoney(summary.net)}
            tone={Number(summary.net) >= 0 ? 'ok' : 'bad'}
            toned
            icon={BarChart3}
            hint="Freebet bahsi kasadan çıkmadığı için nete girmez"
          />
        </div>
      )}

      {/* --- Süzgeçler --------------------------------------------------- */}
      {/* Üst satır her gün sorulan beş soruyu taşır; kalanlar Detaylı süzgeç
          çekmecesindedir. Etkin çipler her zaman çizilir: hangi süzgecin açık
          olduğu görünmezse yönetici kısa listeyi "kupon yok" diye okurdu. */}
      <section className="sbet-filters">
        <div className="sbet-grid sbet-grid-main">
          <Field label="Üye ara">
            <form onSubmit={submitSearch}>
              <Search size={13} aria-hidden="true" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Kullanıcı adı… (Enter)"
                className="admin-input pl-10"
                aria-label="Kullanıcı adı"
              />
            </form>
          </Field>

          <Field label="Kupon no">
            <form onSubmit={submitCouponNo}>
              <Ticket size={13} aria-hidden="true" />
              <input
                value={couponQuery}
                onChange={(event) => setCouponQuery(event.target.value)}
                placeholder="Kupon numarası… (Enter)"
                className="admin-input pl-10"
                aria-label="Kupon numarası"
                title="Numaranın bir parçası da yeterlidir"
              />
            </form>
          </Field>

          <Field label="Kupon durumu">
            <select
              value={status}
              onChange={(event) => { setPage(0); setStatus(event.target.value); }}
              className="admin-input"
              aria-label="Kupon durumu"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Ödeme durumu">
            <select
              value={payoutState}
              onChange={(event) => { setPage(0); setPayoutState(event.target.value); }}
              className="admin-input"
              aria-label="Ödeme durumu"
              title="Kasadan para ödendi mi — kuponun durumundan ayrı bir sorudur"
            >
              {PAYOUT_STATES.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Maç adı">
            <form onSubmit={submitTeam}>
              <Search size={13} aria-hidden="true" />
              <input
                value={teamQuery}
                onChange={(event) => setTeamQuery(event.target.value)}
                placeholder="Takım / maç… (Enter)"
                className="admin-input pl-10"
                aria-label="Maç adı"
                title="Kuponun herhangi bir maçında geçen takım adı"
              />
            </form>
          </Field>

          <Field label="Diğer süzgeçler">
            <button
              type="button"
              onClick={toggleMore}
              aria-expanded={moreOpen}
              aria-controls="sbet-filters-more"
              className={`sbet-toggle sbet-more${moreOpen ? ' is-open' : ''}`}
            >
              <SlidersHorizontal size={13} aria-hidden="true" />
              Detaylı süzgeç
              {moreCount > 0 && (
                <span className="sbet-filters-count">{moreCount}</span>
              )}
              <ChevronDown size={14} aria-hidden="true" className="sbet-more-caret" />
            </button>
          </Field>
        </div>

        <div id="sbet-filters-more" className="sbet-filters-body" hidden={!moreOpen}>
          <div className="sbet-grid">
            {facets.providers.length > 1 && (
              <Field label="Kaynak">
                <select
                  value={provider}
                  onChange={(event) => { setPage(0); setProvider(event.target.value); }}
                  className="admin-input"
                  aria-label="Kaynak"
                  title="Yalnızca kuponu olan kaynaklar listelenir"
                >
                  <option value="">Tüm kaynaklar</option>
                  {facets.providers.map((row) => (
                    <option key={row.value} value={row.value}>{row.label}</option>
                  ))}
                </select>
              </Field>
            )}

            {facets.leagues.length > 0 && (
              <Field label="Lig">
                <select
                  value={league}
                  onChange={(event) => { setPage(0); setLeague(event.target.value); }}
                  className="admin-input"
                  aria-label="Lig"
                >
                  <option value="">Tüm ligler</option>
                  {facets.leagues.map((row) => (
                    <option key={row.value} value={row.value}>
                      {row.value} ({row.legs})
                    </option>
                  ))}
                </select>
              </Field>
            )}

            {facets.sports.length > 0 && (
              <Field label="Spor dalı">
                <select
                  value={sportKey}
                  onChange={(event) => { setPage(0); setSportKey(event.target.value); }}
                  className="admin-input"
                  aria-label="Spor dalı"
                >
                  <option value="">Tüm spor dalları</option>
                  {facets.sports.map((row) => (
                    <option key={row.value} value={row.value}>
                      {row.value} ({row.legs})
                    </option>
                  ))}
                </select>
              </Field>
            )}

            {hasNative && (
              <Field label="Bahis türü">
                <select
                  value={stakeType}
                  onChange={(event) => { setPage(0); setStakeType(event.target.value); }}
                  className="admin-input"
                  aria-label="Bahis türü"
                  title="Freebet kuponunda bahis kasadan çıkmaz; gerçek hacim yalnızca nakit kuponlardadır"
                >
                  {STAKE_TYPES.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </Field>
            )}

            <Field label="Bahis aralığı (₺)">
              <div className="sbet-range">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={minStake}
                  onChange={(event) => { setPage(0); setMinStake(event.target.value); }}
                  placeholder="Min"
                  className="admin-input"
                  aria-label="En düşük bahis"
                />
                <span aria-hidden="true">–</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={maxStake}
                  onChange={(event) => { setPage(0); setMaxStake(event.target.value); }}
                  placeholder="Max"
                  className="admin-input"
                  aria-label="En yüksek bahis"
                />
              </div>
            </Field>

            {hasNative && !override && (
              <Field label="Yönetici müdahalesi">
                <button
                  type="button"
                  onClick={() => { setPage(0); setCustomOnly((current) => !current); }}
                  aria-pressed={customOnly}
                  className="sbet-toggle"
                >
                  <Wand2 size={13} aria-hidden="true" /> Yalnızca özel oran
                </button>
              </Field>
            )}
          </div>

          {/* Tarih süzgeci özeti de daraltır: "bu hafta kaç kişi oynadı"
              sorusunun yanıtı kart rakamlarındadır. */}
          <div className="sbet-filters-dates">
            <DateRangeFilter
              value={dateRange}
              onChange={(value) => { setPage(0); setDateRange(value); }}
            />
          </div>
        </div>

        {activeFilters.length > 0 && (
          <div className="sbet-chips">
            <span className="sbet-chips-label">Etkin</span>
            {activeFilters.map((chip) => (
              <span key={chip.key} className="sbet-chip">
                <span>{chip.label}</span>
                <button
                  type="button"
                  onClick={() => { setPage(0); chip.clear(); }}
                  aria-label={`${chip.label} süzgecini kaldır`}
                >
                  <X size={11} aria-hidden="true" />
                </button>
              </span>
            ))}
            <button
              type="button"
              onClick={clearAllFilters}
              className="sbet-chip-clear"
            >
              <X size={11} aria-hidden="true" /> Tümünü temizle
            </button>
          </div>
        )}
      </section>

      {/* Süzgeçlerin bir kısmının hazır kuponda karşılığı yoktur; liste o
          zaman yalnızca sitenin kendi kuponlarını gösterir. Uç bunu
          bildiriyordu ama panel yazmıyordu: yönetici listeyi eksik değil
          "kupon yok" diye okuyordu. */}
      {!hostedSupported && (
        <div className="sbet-note is-warn">
          <Info size={15} aria-hidden="true" />
          <div className="sbet-note-body">
            Seçili süzgeçlerin hazır spor kitabı kuponlarında karşılığı yok
            (lig/spor dalı, sitenin oran kaynağı, freebet ayrımı ve özel oran
            izi sağlayıcıda kalan kuponda bulunmaz). Liste şu anda{' '}
            <b>yalnızca sitenin kendi kuponlarını</b> gösteriyor.
          </div>
        </div>
      )}

      {exportError && (
        <div className="sbet-note is-bad" role="alert">
          <AlertCircle size={15} aria-hidden="true" />
          <div className="sbet-note-body">{exportError}</div>
        </div>
      )}

      {/* --- Kırılım raporu ---------------------------------------------- */}
      {/* Rapor yalnızca istendiğinde çekilir: liste her süzgeç değişiminde
          yenilenir, bu sorgu ise GROUP BY taraması yapar. */}
      {hasNative && (
      <section className="sbet-report">
        <div className="sbet-report-head">
          <BarChart3 size={15} aria-hidden="true" />
          <span className="sbet-report-title">Kırılım raporu</span>
          <select
            value={reportGroup}
            onChange={(event) => {
              setReportGroup(event.target.value);
              loadReport(event.target.value);
            }}
            className="admin-input"
            aria-label="Rapor kırılımı"
          >
            {REPORT_GROUPS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => (report ? setReport(null) : loadReport(reportGroup))}
            className="admin-btn-secondary"
          >
            {report ? 'Gizle' : 'Raporu getir'}
          </button>
          <span className="sbet-hint">
            Süzgeçlerin tamamı rapora da uygulanır
          </span>
        </div>

        {report && (
          <div className="sbet-report-body">
            {report.loading && (
              <div className="flex items-center gap-2 py-2 text-xs text-slate-500">
                <Loader2 size={13} className="animate-spin" aria-hidden="true" />
                Hesaplanıyor...
              </div>
            )}
            {!report.loading && report.error && (
              <p className="py-2 text-xs text-rose-300">Rapor alınamadı.</p>
            )}
            {!report.loading && !report.error && !report.items.length && (
              <p className="py-2 text-xs text-slate-500">
                Süzgece uyan kupon yok ya da bacak bilgisi henüz devredilmedi.
              </p>
            )}
            {!report.loading && !report.error && !!report.items.length && (
              <>
                <div className="sbet-scroll">
                  <table className="sbet-table" style={{ minWidth: '680px' }}>
                    <thead>
                      <tr>
                        <th>{REPORT_GROUP_LABELS[report.group]}</th>
                        <th>KUPON</th><th>OYUNCU</th><th>BAHİS</th>
                        <th>ÖDEME</th><th>NET</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.items.map((row) => (
                        <tr key={row.value}>
                          <td style={{ fontWeight: 700 }}>
                            {report.group === 'provider'
                              ? providerLabel(row.value)
                              : row.value}
                          </td>
                          <td>{row.coupons}</td>
                          <td>{row.players}</td>
                          <td>{formatMoney(row.stake)}</td>
                          <td style={{ color: 'var(--sbet-ok)' }}>
                            {formatMoney(row.payout)}
                          </td>
                          <td
                            style={{
                              fontWeight: 800,
                              color: row.net >= 0
                                ? 'var(--sbet-ok)' : 'var(--sbet-bad)',
                            }}
                          >
                            {formatMoney(row.net)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Bir kupon birden çok lige dokunabilir; rakamlar toplanınca
                    listenin genel toplamını aşabilir. Yazılmazsa yönetici
                    tabloyu hatalı sanar. */}
                <p className="sbet-report-note">
                  Toplamlar kupon başınadır. Birden çok{' '}
                  {REPORT_GROUP_LABELS[report.group].toLowerCase()} içeren
                  kupon her satırda bir kez sayılır, bu yüzden kırılım
                  toplamları genel toplamdan büyük olabilir.
                </p>
              </>
            )}
          </div>
        )}
      </section>
      )}

      {/* --- Toplu işlem -------------------------------------------------- */}
      {canSettle && !!selected.size && (
        <div className="sbet-bulk">
          <span className="sbet-bulk-count">
            <CheckCheck size={14} aria-hidden="true" />
            {selected.size} kupon seçildi
          </span>
          {SETTLE_ACTIONS.map((action) => (
            <button
              key={action.value}
              type="button"
              onClick={() => {
                setSettleResult('');
                setSettleNote('');
                setSettleRequest({
                  action: action.value,
                  couponIds: Array.from(selected),
                });
              }}
              data-tone={action.value}
              className={`admin-btn-secondary ${action.tone}`}
            >
              {action.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="admin-btn-secondary sbet-bulk-drop"
          >
            <X size={11} aria-hidden="true" /> Seçimi bırak
          </button>
        </div>
      )}

      {settleResult && (
        <div className="sbet-note is-info" role="status">
          <Info size={15} aria-hidden="true" />
          <div className="sbet-note-body">{settleResult}</div>
        </div>
      )}

      {/* --- Liste ------------------------------------------------------- */}
      <section className="sbet-list">
        <div className="sbet-list-head">
          <span className="sbet-list-title">
            <Ticket size={14} aria-hidden="true" />
            Kuponlar
          </span>
          <div className="sbet-list-meta">
            <span>
              {total
                ? `${rangeFrom}–${rangeTo} / ${total} kupon`
                : 'Kayıt yok'}
            </span>
            {/* Dosya ekrandaki süzgeçlerin tamamını taşır; sayfalama
                uygulanmaz, sonuç kümesinin tamamı yazılır. */}
            <button
              type="button"
              onClick={exportCsv}
              disabled={exporting || !total}
              className="admin-btn-secondary disabled:opacity-40"
              title="Süzgece uyan bütün kuponları CSV olarak indir (Excel uyumlu)"
            >
              {exporting
                ? <Loader2 size={11} className="animate-spin" aria-hidden="true" />
                : <Download size={11} aria-hidden="true" />}
              CSV indir
            </button>
            <button
              type="button"
              onClick={load}
              className="admin-btn-secondary"
            >
              <RefreshCw size={11} aria-hidden="true" /> Yenile
            </button>
          </div>
        </div>

        {loading && (
          <div className="sbet-skeleton" role="status" aria-label="Kuponlar yükleniyor">
            <span className="sr-only">Kuponlar yükleniyor</span>
            {Array.from({ length: 6 }, (_, index) => (
              <div className="sbet-skeleton-row" key={index}>
                <span /><span /><span /><span /><span /><span /><span />
              </div>
            ))}
          </div>
        )}

        {!loading && loadError && (
          <div className="sbet-state" role="alert">
            <AlertCircle size={26} style={{ color: 'var(--sbet-bad)' }} aria-hidden="true" />
            <h2>Kuponlar alınamadı</h2>
            <p>{loadError}</p>
            <button
              type="button"
              onClick={load}
              className="admin-btn-secondary"
            >
              <RefreshCw size={13} aria-hidden="true" /> Tekrar dene
            </button>
          </div>
        )}

        {!loading && !loadError && items.length === 0 && (
          <div className="sbet-state">
            <Inbox size={26} style={{ color: 'var(--text-muted)' }} aria-hidden="true" />
            <h2>
              {override ? 'Bu özel oranı henüz kimse oynamadı' : 'Kupon bulunamadı'}
            </h2>
            <p>
              {override
                ? 'Tanım yayına yeni girmiş olabilir; oynanan ilk kupon burada görünür.'
                : 'Seçili süzgeçlere uyan kupon yok. Tarih aralığını genişletmeyi ya da süzgeçleri temizlemeyi deneyin.'}
            </p>
            {!override && activeFilters.length > 0 && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="admin-btn-secondary"
              >
                <X size={13} aria-hidden="true" /> Süzgeçleri temizle
              </button>
            )}
          </div>
        )}

        {!loading && !loadError && items.length > 0 && (
          <div className="sbet-scroll">
            <table className="sbet-table">
              <thead>
                <tr>
                  {canSettle && (
                    <th className="sbet-pick-cell">
                      <input
                        type="checkbox"
                        checked={allOpenSelected}
                        onChange={toggleAll}
                        disabled={!openCoupons.length}
                        aria-label="Sonuçlanmamış kuponların tümünü seç"
                        title="Yalnızca sonuçlanmamış kuponlar seçilir"
                      />
                    </th>
                  )}
                  <th>Tarih</th>
                  <th>Kupon No</th>
                  <th>Üye</th>
                  <th>Seçimler</th>
                  <th>Bahis</th>
                  <th>Oran</th>
                  <th>Ödeme / Net</th>
                  <th>Durum</th>
                  <th className="sbet-cell-right" aria-label="Kupon detayı" />
                </tr>
              </thead>
              <tbody>
                {items.map((coupon) => {
                  const when = dateParts(coupon.created_at);
                  const net = couponNet(coupon);
                  const netClass = net === null
                    ? '' : (net >= 0 ? 'is-plus' : 'is-minus');
                  const netText = net === null
                    ? '' : `${net >= 0 ? '+' : ''}${formatMoney(net)}`;

                  // Hazır spor kitabı kuponu (Flexrix / Betby) aynı listededir
                  // ama bacakları, cashout'u ve elle sonuçlandırması bizde
                  // yoktur: kupon sağlayıcıda durur, bize para hareketi gelir.
                  if (coupon.source === 'hosted') {
                    const hosted = hostedBadge(coupon.status);
                    const paid = coupon.status === 'void'
                      ? coupon.refund : coupon.payout;
                    return (
                      <tr key={coupon.id}>
                        {canSettle && (
                          <td className="sbet-pick-cell">
                            <input
                              type="checkbox"
                              checked={false}
                              disabled
                              aria-label="Hazır spor kitabı kuponu seçilemez"
                              title="Bu kuponu sağlayıcı sonuçlandırır; panelden karara bağlanamaz"
                              readOnly
                            />
                          </td>
                        )}
                        <td>
                          <div className="sbet-when">
                            <b>{when.day}</b>
                            <span>{when.time}</span>
                          </div>
                        </td>
                        <td><CouponNo coupon={coupon} /></td>
                        <td>
                          {coupon.user_id ? (
                            <Link
                              to={`/admin/users/${coupon.user_id}`}
                              className="sbet-user"
                            >
                              <span className="sbet-avatar" aria-hidden="true">
                                {initials(coupon.username)}
                              </span>
                              {coupon.username || '—'}
                            </Link>
                          ) : <span className="sbet-user-none">—</span>}
                        </td>
                        <td>
                          <div className="sbet-legs">
                            <div className="sbet-leg-head">
                              <span className="sbet-tag is-hosted">
                                {coupon.provider_label}
                              </span>
                              {coupon.is_live && (
                                <span className="sbet-tag is-live">Canlı</span>
                              )}
                              {coupon.selection_count > 0 && (
                                <span className="sbet-tag">
                                  {coupon.selection_count} maç
                                </span>
                              )}
                            </div>
                            <HostedBetSelections
                              item={coupon}
                              loading={!!busy[coupon.id]}
                              error={errors[coupon.id] || ''}
                              onRetry={retry}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="sbet-money">
                            <b>{formatMoney(coupon.stake)}</b>
                          </div>
                        </td>
                        <td>
                          {Number(coupon.odds) > 0
                            ? <span className="sbet-odd">{Number(coupon.odds).toFixed(2)}</span>
                            : (
                              <span
                                className="sbet-odd is-unknown"
                                title="Oran sağlayıcının kupon bildiriminden gelir; bildirim kapalıysa bilinmez"
                              >
                                —
                              </span>
                            )}
                        </td>
                        <td>
                          <div className="sbet-money">
                            <b className={Number(paid) > 0 ? 'is-paid' : 'is-idle'}>
                              {formatMoney(paid)}
                            </b>
                            {netText && (
                              <small className={netClass}>{netText}</small>
                            )}
                          </div>
                        </td>
                        <td>
                          <span
                            className={`badge-pill ${hosted.cls}`}
                            title={coupon.status === 'pending' ? PENDING_HINT : undefined}
                          >
                            {hosted.label}
                          </span>
                          {coupon.manual && (
                            <span className="sbet-tag is-hosted">Manuel sonuçlandırıldı</span>
                          )}
                        </td>
                        <td className="sbet-cell-right">
                          <Link
                            to={`/admin/sports-bets/hosted/${coupon.provider}/${encodeURIComponent(coupon.bet_id)}`}
                            className="sbet-go"
                            title="Kupon detayı: bacaklar, para hareketleri ve sağlayıcı bildirimleri"
                          >
                            İncele <ChevronRight size={11} aria-hidden="true" />
                          </Link>
                        </td>
                      </tr>
                    );
                  }

                  const badge = STATUS_LABELS[coupon.status] || STATUS_LABELS.active;
                  const settled = coupon.status === 'won' || coupon.status === 'cashed_out';
                  const picked = selected.has(coupon.id);
                  // Ödeme hücresi üç durumdan birini gösterir. Sonuçlanmamış
                  // kuponda rakam ödenen para değil **muhtemel** kazançtır;
                  // altındaki etiket bunu yazar, yoksa liste ödenmemiş bir
                  // tutarı ödenmiş gibi okutur.
                  let payoutValue = coupon.possible_win;
                  if (settled) payoutValue = coupon.payout;
                  else if (coupon.status === 'lost') payoutValue = 0;
                  const payoutPending = coupon.status === 'active';
                  return (
                    <tr key={coupon.id} className={picked ? 'is-picked' : ''}>
                      {canSettle && (
                        <td className="sbet-pick-cell">
                          {/* Sonuçlanmış kupon seçilemez: karar yalnızca açık
                              kupona uygulanır, kapanmış olan zaten atlanırdı. */}
                          <input
                            type="checkbox"
                            checked={picked}
                            onChange={() => toggleOne(coupon.id)}
                            disabled={coupon.status !== 'active'}
                            aria-label={`${coupon.username || 'Kupon'} kuponunu seç`}
                          />
                        </td>
                      )}
                      <td>
                        <div className="sbet-when">
                          <b>{when.day}</b>
                          <span>{when.time}</span>
                        </div>
                      </td>
                      <td><CouponNo coupon={coupon} /></td>
                      <td>
                        {/* Yönetici listeden üyenin tamamına geçebilmeli: özel
                            oranı oynayanı görüp hesabını incelemek tek akıştır. */}
                        <Link
                          to={`/admin/users/${coupon.user_id}`}
                          className="sbet-user"
                        >
                          <span className="sbet-avatar" aria-hidden="true">
                            {initials(coupon.username)}
                          </span>
                          {coupon.username || '—'}
                        </Link>
                      </td>
                      <td>
                        <div className="sbet-legs">
                          {(coupon.leg_count > 1 || coupon.custom_odd_count > 0) && (
                            <div className="sbet-leg-head">
                              {coupon.leg_count > 1 && (
                                <span className="sbet-tag">
                                  {coupon.leg_count} maç
                                </span>
                              )}
                              {coupon.custom_odd_count > 0 && (
                                <span className="sbet-tag" style={{
                                  borderColor: 'rgba(251,191,36,0.3)',
                                  color: 'var(--sbet-magic)',
                                }}
                                >
                                  {coupon.custom_odd_count} özel oran
                                </span>
                              )}
                            </div>
                          )}
                          {(coupon.picks || []).map((pick, index) => (
                            <div
                              key={`${coupon.id}-${index}`}
                              className={`sbet-leg is-${pick.result || 'pending'}`}
                            >
                              <span className="sbet-leg-event">
                                {pick.home} - {pick.away}
                              </span>
                              <span className={PICK_RESULT_CLASS[pick.result] || 'text-slate-400'}>
                                {pickLabel(pick)}
                              </span>
                              <span className="sbet-leg-odd">
                                {Number(pick.odd).toFixed(2)}
                              </span>
                              {/* Sağlayıcının ham fiyatı da saklanır:
                                  yöneticinin müdahalesinin büyüklüğü kupon
                                  satırından okunmalı. */}
                              {pick.custom_odd && (
                                <span
                                  className="sbet-magic"
                                  title={pick.base_odd
                                    ? `Sağlayıcı fiyatı ${Number(pick.base_odd).toFixed(2)}`
                                    : 'Panelden geçirilmiş özel oran'}
                                >
                                  <Wand2 size={9} aria-hidden="true" />
                                  {pick.base_odd
                                    ? Number(pick.base_odd).toFixed(2) : 'özel'}
                                </span>
                              )}
                              {pick.score && (
                                <span className="sbet-leg-score">({pick.score})</span>
                              )}
                            </div>
                          ))}
                          {!(coupon.picks || []).length && (
                            <span className="sbet-leg-empty">Seçim bilgisi yok</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="sbet-money">
                          <b>{formatMoney(coupon.stake)}</b>
                          {coupon.freebet && (
                            <span
                              className="sbet-tag is-free"
                              title="Freebet hakkıyla oynandı: bakiyeden para düşülmedi, kazançta bahis tutarı iade edilmez"
                            >
                              Freebet
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="sbet-odd">
                          {Number(coupon.total_odd).toFixed(2)}
                        </span>
                      </td>
                      <td>
                        <div className="sbet-money">
                          <b
                            className={settled && Number(coupon.payout) > 0
                              ? 'is-paid' : 'is-idle'}
                            title={payoutPending
                              ? 'Kupon sonuçlanmadı; gösterilen tutar muhtemel kazançtır'
                              : undefined}
                          >
                            {formatMoney(payoutValue)}
                          </b>
                          {payoutPending && <small>muhtemel</small>}
                          {!payoutPending && netText && (
                            <small className={netClass}>{netText}</small>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`badge-pill ${badge.cls}`}>{badge.label}</span>
                      </td>
                      <td className="sbet-cell-right">
                        {/* Satırın tamamı tıklanabilir yapılmadı: hücrede zaten
                            üye profiline giden bir bağlantı var, iç içe iki
                            hedef yanlış sayfayı açardı. */}
                        <Link
                          to={`/admin/sports-bets/${coupon.id}`}
                          className="sbet-go"
                          title="Kupon detayı: bacaklar, para hareketleri ve işlem izi"
                        >
                          İncele <ChevronRight size={11} aria-hidden="true" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !loadError && total > 0 && (
          <div className="sbet-pager">
            <span className="sbet-pager-info">
              {rangeFrom}–{rangeTo} / {total} kupon · sayfa {page + 1}/{pageCount}
            </span>
            <label className="sbet-pager-size">
              Sayfa boyutu
              <select
                value={pageSize}
                onChange={(event) => {
                  const size = Number(event.target.value);
                  setPage(0);
                  setPageSize(size);
                  writePageSize('coupons', size);
                }}
                className="admin-input"
                aria-label="Sayfa boyutu"
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="sbet-pager-nav">
              <button
                type="button"
                onClick={() => setPage((current) => Math.max(0, current - 1))}
                disabled={page === 0}
                className="sbet-pager-btn"
                aria-label="Önceki sayfa"
              >
                <ChevronLeft size={13} aria-hidden="true" />
              </button>
              {pageWindow(page, pageCount).map((value, index) => (
                value === 'gap' ? (
                  // eslint-disable-next-line react/no-array-index-key
                  <span key={`gap-${index}`} className="sbet-pager-gap">…</span>
                ) : (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setPage(value)}
                    className={`sbet-pager-btn ${value === page ? 'is-current' : ''}`}
                    aria-current={value === page ? 'page' : undefined}
                    aria-label={`${value + 1}. sayfa`}
                  >
                    {value + 1}
                  </button>
                )
              ))}
              <button
                type="button"
                onClick={() => setPage((current) => current + 1)}
                disabled={page + 1 >= pageCount}
                className="sbet-pager-btn"
                aria-label="Sonraki sayfa"
              >
                <ChevronRight size={13} aria-hidden="true" />
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Karar gerçek para hareketi doğurur; onay penceresi ne olacağını
          açıkça yazar ve isteğe bağlı bir gerekçe alır. */}
      <ConfirmDialog
        open={!!settleRequest}
        busy={settling}
        tone={settleRequest?.action === 'won' ? 'warning' : 'danger'}
        title={settleRequest
          ? `${settleRequest.couponIds.length} kupon ${
            settleAction(settleRequest.action).label.toLowerCase()}`
          : ''}
        description={settleRequest
          ? `${settleAction(settleRequest.action).warning} Bu işlem geri `
            + 'alınamaz ve denetim izine yazılır.'
          : ''}
        confirmLabel={settleRequest
          ? settleAction(settleRequest.action).label : 'Onayla'}
        extra={(
          <input
            value={settleNote}
            onChange={(event) => setSettleNote(event.target.value)}
            placeholder="Gerekçe (isteğe bağlı, ledger notuna yazılır)"
            className="admin-input w-full"
            maxLength={255}
            aria-label="Sonuçlandırma gerekçesi"
          />
        )}
        onCancel={() => { setSettleRequest(null); setSettleNote(''); }}
        onConfirm={runSettle}
      />
    </>
  );
};

export default SportsCoupons;
